import React, { useMemo } from "react";
import {
    StandardModalHeader,
    StandardModalFooter,
    PlainModal,
} from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { pluralizeWithCount } from "@common/helpers/string";
import { useRemoveOpeningBalanceMutation } from "@api/openingBalance.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useBusinessNamesFor } from "./reachCopy";

interface RemoveOpeningBalanceTarget {
    entityId: number;
    accountId: string;
}

interface RemoveOpeningBalanceModalProps {
    show?: boolean;
    onHide?: () => void;
    accountName: string;
    // One DELETE each.
    targets: RemoveOpeningBalanceTarget[];
    // A business not in `targets` lands in the nothing-will-change block.
    rowEntityIds: number[];
    onRemoved?: () => void;
    onMutation?: (promise: Promise<unknown>) => void;
}

export const RemoveOpeningBalanceModal: React.FC<
    RemoveOpeningBalanceModalProps
> = ({
    show,
    onHide,
    accountName,
    targets,
    rowEntityIds,
    onRemoved,
    onMutation,
}) => {
    const { mutateAsync, isPending } = useRemoveOpeningBalanceMutation();
    const { toast, error: toastError } = useToaster();
    const businessNamesFor = useBusinessNamesFor();

    const removedEntityIds = useMemo(
        () => Array.from(new Set(targets.map((target) => target.entityId))),
        [targets],
    );
    const unchangedEntityIds = useMemo(() => {
        const removed = new Set(removedEntityIds);
        return rowEntityIds.filter((entityId) => !removed.has(entityId));
    }, [rowEntityIds, removedEntityIds]);

    const removedNames = businessNamesFor(removedEntityIds);
    const unchangedNames = businessNamesFor(unchangedEntityIds);

    const handleConfirm = async () => {
        if (targets.length === 0) {
            return;
        }
        // Track before the sequence starts so a drawer close mid-request still
        // sees the write. Returning the promise lets ButtonWithLoader ignore
        // clicks until it settles.
        const sequence = (async () => {
            for (const [index, target] of targets.entries()) {
                try {
                    await mutateAsync({
                        entityId: target.entityId,
                        accountId: target.accountId,
                    });
                } catch {
                    toastError("Failed to remove opening balance");
                    // A later failure leaves earlier deletes committed: close and
                    // let the editor resync, but do not replay this open's targets.
                    if (index > 0) {
                        onRemoved?.();
                        onHide?.();
                    }
                    return;
                }
            }
            toast("Opening balance removed");
            onRemoved?.();
            onHide?.();
        })();
        onMutation?.(sequence);
        await sequence;
    };

    return (
        <PlainModal show={show} onHide={onHide} size="md">
            <StandardModalHeader>Remove opening balance?</StandardModalHeader>
            <p>
                {`This will permanently remove the opening balance for ${accountName}. This action cannot be undone.`}
            </p>
            {removedNames.length > 0 && (
                <div>
                    <p className="mb-1">{`Will be removed in ${pluralizeWithCount(
                        "business",
                        removedNames.length,
                    )}:`}</p>
                    <ul>
                        {removedNames.map((name, index) => (
                            <li key={`removed-${removedEntityIds[index]}`}>
                                {name}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {unchangedNames.length > 0 && (
                <div>
                    <p className="mb-1">{`Nothing will change in ${pluralizeWithCount(
                        "business",
                        unchangedNames.length,
                    )}:`}</p>
                    <ul>
                        {unchangedNames.map((name, index) => (
                            <li key={`unchanged-${unchangedEntityIds[index]}`}>
                                {name}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <StandardModalFooter className="mt-4">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onHide}
                    size="xl"
                    disabled={isPending}
                >
                    Cancel
                </Button>
                <ButtonWithLoader
                    variant="primary"
                    type="button"
                    loading={isPending}
                    size="xl"
                    onClick={handleConfirm}
                >
                    Remove
                </ButtonWithLoader>
            </StandardModalFooter>
        </PlainModal>
    );
};
